/**
 * Composer for making and changing files with Mochi (spec §5.8.5), in the
 * design's look (weco2/files-mochi/fm-core.jsx `FMComposer`): an
 * auto-growing textarea, then a row of Upload · Pick from Files · Voice note
 * · hint · Talk to Mochi · Send.
 *
 * Up to 8 attachments — photos, PDFs, Office files, CSV, text — come from the
 * paperclip, drag & drop, paste, or the Files picker. New files upload at once
 * to the person's Drive; Send waits until every upload has finished. A voice
 * note is transcribed by the assistant and added to the message (Mochi reads
 * words, not audio files). The wave button opens a voice call with Mochi.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type DragEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { useTranslation } from "react-i18next";
import { ArrowUp, FileText, FolderOpen, Mic, Paperclip, Sparkles, X } from "lucide-react";

import { transcribeAudio } from "../../../modules/assistant/api/voiceApi";
import { useVoiceRecorder } from "../../../modules/assistant/hooks/useVoiceRecorder";
import { FMVoice } from "../fm/FMVoice";
import { FMSpin, FMWaveButton, cx } from "../fm/primitives";
import {
  ATTACH_ACCEPT,
  MAX_ATTACHMENTS,
  MAX_ATTACHMENT_BYTES,
  formatSize,
  isAcceptedFile,
  uploadAttachment,
  type Attachment,
} from "./attachments";
import { FilesPicker, type PickedFile } from "./FilesPicker";

export interface MochiComposerVoice {
  /** Shown on the call's pill ("Create with Mochi", the file's name…). */
  where?: string;
  /** One spoken turn → Mochi's reply text. */
  onTurn: (text: string) => Promise<string | null | undefined>;
}

export interface MochiComposerProps {
  onSend: (text: string, attachments: Attachment[]) => void;
  busy?: boolean;
  disabled?: boolean;
  placeholder: string;
  autoFocus?: boolean;
  /** "lg" on the Create page, "sm" in the panels ("large"/"small" still work). */
  size?: "lg" | "sm" | "large" | "small";
  footer?: ReactNode;
  /** Muted helper beside the tools (design: "Attach photos or files — …"). */
  hint?: string | null;
  initialText?: string;
  /** Files already in Drive to start with (remount with a new `key` to reset). */
  initialFiles?: PickedFile[];
  /** Called whenever the list of attachments changes (before sending). */
  onAttachmentsChange?: (attachments: Attachment[]) => void;
  /** Text allowed with no words when files are attached. */
  allowFilesOnly?: boolean;
  /** Offer a voice call with Mochi (the wave button). */
  voice?: MochiComposerVoice;
  inputRef?: RefObject<HTMLTextAreaElement | null>;
  className?: string;
}

let seq = 0;
const nextKey = () => `att-${++seq}`;
const clock = (ms: number) => {
  const n = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, "0")}`;
};

export function MochiComposer({
  onSend,
  busy = false,
  disabled = false,
  placeholder,
  autoFocus,
  size = "lg",
  footer,
  hint,
  initialText = "",
  initialFiles,
  onAttachmentsChange,
  allowFilesOnly = true,
  voice,
  inputRef,
  className,
}: MochiComposerProps) {
  const { t, i18n } = useTranslation();
  const lg = size === "lg" || size === "large";
  const [text, setText] = useState(initialText);
  const [attachments, setAttachments] = useState<Attachment[]>(() =>
    (initialFiles ?? []).slice(0, MAX_ATTACHMENTS).map((p) => ({
      key: nextKey(),
      name: p.name,
      size: p.size,
      fileId: p.id,
      status: "ready" as const,
    })),
  );
  const [dragging, setDragging] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [recErr, setRecErr] = useState("");
  const [transcribing, setTranscribing] = useState(false);
  const recorder = useVoiceRecorder();
  const recording = recorder.status === "recording" || recorder.status === "requesting";
  const fileRef = useRef<HTMLInputElement | null>(null);
  const ownArea = useRef<HTMLTextAreaElement | null>(null);
  const areaRef = inputRef ?? ownArea;
  const listRef = useRef<Attachment[]>([]);
  listRef.current = attachments;

  useEffect(() => {
    onAttachmentsChange?.(attachments);
  }, [attachments, onAttachmentsChange]);

  // Free thumbnails when the composer goes away.
  useEffect(
    () => () => {
      for (const a of listRef.current) if (a.preview) URL.revokeObjectURL(a.preview);
    },
    [],
  );

  // Auto-grow (design: 180px max on the Create page, 120px in panels).
  const maxHeight = lg ? 180 : 120;
  useLayoutEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(maxHeight, el.scrollHeight)}px`;
  }, [text, maxHeight, areaRef]);

  useEffect(() => {
    if (recorder.error)
      setRecErr(t("filesMochi.composer.micNeeded", "Allow microphone access to record a voice note."));
  }, [recorder.error, t]);

  const patch = (key: string, change: Partial<Attachment>) =>
    setAttachments((list) => list.map((a) => (a.key === key ? { ...a, ...change } : a)));

  const addFiles = useCallback(
    (files: File[]) => {
      if (disabled) return;
      const room = MAX_ATTACHMENTS - listRef.current.length;
      const accepted = files.filter(isAcceptedFile);
      if (accepted.length < files.length) {
        setNotice(
          t("mochi.create.composer.unsupported", "Some files were skipped — attach photos, PDFs, Word, PowerPoint, Excel, CSV or text files."),
        );
      }
      if (accepted.length > room) {
        setNotice(t("mochi.create.composer.tooMany", { defaultValue: "You can attach up to {{count}} files.", count: MAX_ATTACHMENTS }));
      }
      for (const file of accepted.slice(0, Math.max(0, room))) {
        const key = nextKey();
        const preview = file.type.startsWith("image/") && typeof URL.createObjectURL === "function" ? URL.createObjectURL(file) : null;
        const tooBig = file.size > MAX_ATTACHMENT_BYTES;
        setAttachments((list) => [
          ...list,
          {
            key,
            name: file.name,
            size: file.size,
            fileId: null,
            status: tooBig ? "error" : "uploading",
            error: tooBig ? t("mochi.create.composer.tooBig", "Too large (30 MB max)") : undefined,
            preview,
          },
        ]);
        if (tooBig) continue;
        uploadAttachment(file)
          .then((item) => patch(key, { fileId: item.id, status: "ready" }))
          .catch(() => patch(key, { status: "error", error: t("mochi.create.composer.uploadFailed", "Upload failed") }));
      }
    },
    [disabled, t],
  );

  const addPicked = (picked: PickedFile[]) => {
    const have = new Set(listRef.current.map((a) => a.fileId));
    const room = MAX_ATTACHMENTS - listRef.current.length;
    const fresh = picked.filter((p) => !have.has(p.id)).slice(0, Math.max(0, room));
    setAttachments((list) => [
      ...list,
      ...fresh.map((p) => ({ key: nextKey(), name: p.name, size: p.size, fileId: p.id, status: "ready" as const })),
    ]);
  };

  const remove = (key: string) =>
    setAttachments((list) => {
      const gone = list.find((a) => a.key === key);
      if (gone?.preview) URL.revokeObjectURL(gone.preview);
      return list.filter((a) => a.key !== key);
    });

  const toggleRecord = async () => {
    if (disabled) return;
    if (recorder.status === "recording") {
      const blob = await recorder.stop();
      if (!blob) return;
      setTranscribing(true);
      try {
        const words = await transcribeAudio(blob, (i18n.language || "").split("-")[0] || undefined);
        if (words) setText((v) => (v.trim() ? `${v.trimEnd()} ${words}` : words));
        requestAnimationFrame(() => areaRef.current?.focus());
      } catch {
        setRecErr(t("filesMochi.composer.transcribeFailed", "That voice note couldn’t be written out. Try again?"));
      } finally {
        setTranscribing(false);
      }
      return;
    }
    if (recorder.status !== "idle") return;
    setRecErr("");
    await recorder.start();
  };

  const uploading = attachments.some((a) => a.status === "uploading");
  const ready = attachments.filter((a) => a.status === "ready");
  const hasText = text.trim().length > 0;
  const canSend = !disabled && !busy && !uploading && !transcribing && (hasText || (allowFilesOnly && ready.length > 0));
  const full = attachments.length >= MAX_ATTACHMENTS;

  const send = () => {
    if (!canSend) return;
    onSend(text.trim(), ready);
    setText("");
    setNotice(null);
    for (const a of attachments) if (a.preview) URL.revokeObjectURL(a.preview);
    setAttachments([]);
    requestAnimationFrame(() => areaRef.current?.focus());
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    addFiles(Array.from(e.dataTransfer?.files ?? []));
  };

  const onPaste = (e: ClipboardEvent) => {
    const files = Array.from(e.clipboardData?.files ?? []);
    if (files.length) {
      e.preventDefault();
      addFiles(files);
    }
  };

  const sendLabel = uploading
    ? t("mochi.create.composer.waiting", "Waiting for attachments to upload")
    : t("mochi.create.composer.send", "Send");
  const uploadLabel = t("filesMochi.composer.upload", "Upload from computer");
  const pickLabel = t("filesMochi.composer.pick", "Pick from Files");
  const recLabel =
    recorder.status === "recording"
      ? t("filesMochi.composer.recStop", "Stop and write out the voice note")
      : t("filesMochi.composer.rec", "Record a voice note");
  const tool = { width: 32, height: 32 };

  return (
    <div
      className={cx("fm fm-comp", dragging && "is-drag", disabled && "opacity-60", className)}
      style={{ borderRadius: lg ? 22 : 20 }}
      onDragOver={(e) => {
        if (disabled) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      data-testid="mochi-composer"
    >
      {dragging && <div className="fm-comp-drop">{t("mochi.create.composer.drop", "Drop to attach")}</div>}
      {attachments.length > 0 && (
        <ul className="flex flex-wrap gap-2 px-4 pt-3" aria-label={t("mochi.create.composer.attached", "Attached files")}>
          {attachments.map((a) => (
            <li key={a.key} className={cx("fm-att", a.status === "error" && "is-err")}>
              {a.preview ? (
                <img src={a.preview} alt="" />
              ) : a.status === "uploading" ? (
                <FMSpin size={12} />
              ) : a.status === "error" ? (
                <FileText size={12} aria-hidden />
              ) : (
                <Paperclip size={12} aria-hidden />
              )}
              <span className="min-w-0 truncate" dir="auto">
                {a.name}
              </span>
              <span className="shrink-0 text-[11px] opacity-70">
                {a.status === "uploading"
                  ? t("mochi.create.composer.uploading", "Uploading…")
                  : a.status === "error"
                    ? a.error
                    : formatSize(a.size)}
              </span>
              <button
                type="button"
                onClick={() => remove(a.key)}
                aria-label={t("mochi.create.composer.remove", { defaultValue: "Remove {{name}}", name: a.name })}
              >
                <X size={11} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
      <textarea
        ref={areaRef}
        value={text}
        dir="auto"
        rows={lg ? 2 : 1}
        disabled={disabled}
        autoFocus={autoFocus}
        maxLength={4000}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(e) => setText(e.target.value)}
        onPaste={onPaste}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            send();
          }
        }}
        style={{
          fontSize: lg ? 15 : 14,
          padding: lg ? "18px 20px 6px" : "12px 16px 4px",
          minHeight: lg ? 72 : 40,
          maxHeight,
        }}
      />
      {notice && (
        <p className="fm-comp-note" role="status">
          {notice}
        </p>
      )}
      <div
        className="fm-comp-row flex items-center gap-1"
        style={{ padding: lg ? "4px 10px 10px" : "2px 8px 8px", justifyContent: "flex-end" }}
      >
        <input
          ref={fileRef}
          type="file"
          multiple
          accept={ATTACH_ACCEPT}
          className="hidden"
          data-testid="mochi-composer-file"
          onChange={(e) => {
            addFiles(Array.from(e.target.files ?? []));
            e.target.value = "";
          }}
        />
        <button
          type="button"
          className="fm-ibtn"
          style={tool}
          disabled={disabled || full}
          onClick={() => fileRef.current?.click()}
          aria-label={uploadLabel}
          title={uploadLabel}
        >
          <Paperclip size={16} aria-hidden />
        </button>
        <button
          type="button"
          className="fm-ibtn"
          style={tool}
          disabled={disabled || full}
          onClick={() => setPickerOpen(true)}
          aria-label={pickLabel}
          title={pickLabel}
        >
          <FolderOpen size={16} aria-hidden />
        </button>
        <button
          type="button"
          className={cx("fm-ibtn", recording && "is-rec")}
          style={
            recording
              ? { width: "auto", height: 32, padding: "0 10px", gap: 6, background: "#FDECEC", color: "#C8282F", fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center" }
              : tool
          }
          disabled={disabled || transcribing}
          onClick={() => void toggleRecord()}
          aria-pressed={recording}
          aria-label={recLabel}
          title={recLabel}
        >
          {transcribing ? (
            <FMSpin size={14} />
          ) : recording ? (
            <>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: "#C8282F" }} />
              {clock(recorder.durationMs)}
            </>
          ) : (
            <Mic size={16} aria-hidden />
          )}
        </button>
        {recErr && (
          <span role="status" className="ms-1 truncate text-[12px] text-[#C8282F]">
            {recErr}
          </span>
        )}
        {transcribing && !recErr && (
          <span role="status" className="ms-1 truncate text-[13px] text-[#9AA0AE]">
            {t("filesMochi.composer.transcribing", "Writing out your voice note…")}
          </span>
        )}
        {hint && !recErr && !recording && !transcribing && (
          <span className="ms-1 hidden min-w-0 items-center gap-1.5 text-[13px] text-[#9AA0AE] sm:flex">
            <Sparkles size={13} className="shrink-0" aria-hidden />
            <span className="truncate">{hint}</span>
          </span>
        )}
        <div className="min-w-0 flex-1">{footer}</div>
        {voice && (
          <span className="ms-1">
            <FMWaveButton size={lg ? 36 : 32} disabled={disabled} onClick={() => setVoiceOpen(true)} />
          </span>
        )}
        <button
          type="button"
          onClick={send}
          disabled={!canSend}
          aria-label={sendLabel}
          title={sendLabel}
          className={cx("fm-send", canSend && "is-on")}
        >
          {(busy || uploading) && !canSend ? <FMSpin /> : <ArrowUp size={16} aria-hidden />}
        </button>
      </div>
      {voice && <FMVoice open={voiceOpen} onClose={() => setVoiceOpen(false)} where={voice.where} onTurn={voice.onTurn} />}
      {pickerOpen && (
        <FilesPicker
          max={MAX_ATTACHMENTS - attachments.length}
          onClose={() => setPickerOpen(false)}
          onPick={(files) => {
            addPicked(files);
            setPickerOpen(false);
          }}
        />
      )}
    </div>
  );
}
